import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
  forwardRef,
  inject,
  computed,
  Signal,
  input,
  linkedSignal,
  output
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateDirective, TranslatePipe as NgxTranslatePipe } from '@ngx-translate/core';
import { map } from 'rxjs';

import { SecuritySymbol } from '../../../../domain/symbol.domain';
import { Watchlist } from '../../../../domain/watchlist.domain';
import { AddToWatchlist } from '../domain';
import { DividerComponent } from '../../../../components/panel/divider.component';
import { IconComponent } from '../../../../components/icon/icon.component';
import { ButtonComponent } from '../../../../components/button/button.component';
import { FlyoutMenuToggleDirective } from '../../../../components/flyout-menu/flyout-menu-toggle.directive';
import { FlyoutMenuComponent } from '../../../../components/flyout-menu/flyout-menu.component';
import { FlyoutMenuItemDirective } from '../../../../components/flyout-menu/flyout-menu-item.directive';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { WatchlistService } from '../../../../services/watchlist/watchlist.service';
import { WatchlistUtils } from '../../../../services/watchlist/watchlist-utils';
import { MediaQueryService, Breakpoint, BreakpointState } from '../../../../services/media-query.service';

export type AddToWatchlistType = 'dropdown' | 'list';
export type AddToWatchlistPosition = 'start-bottom' | 'end-bottom' | 'start-top' | 'end-top';

@Component({
  selector: 'td-wb-add-to-watchlist',
  templateUrl: 'add-to-watchlist.component.html',
  styleUrls: ['add-to-watchlist.component.scss'],
  providers: [
    { provide: AddToWatchlist, useExisting: forwardRef(() => AddToWatchlistComponent) }
  ],
  imports: [
    NgTemplateOutlet,
    DividerComponent,
    IconComponent,
    TranslateDirective,
    NgxTranslatePipe,
    ButtonComponent,
    FlyoutMenuToggleDirective,
    FlyoutMenuComponent,
    FlyoutMenuItemDirective,
    TranslatePipe
  ],
  host: {
    '[class.td-wb-add-to-watchlist--stretched]': 'stretched()'
  }
})
export class AddToWatchlistComponent implements OnInit, AddToWatchlist {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly watchlistService = inject(WatchlistService);

  readonly watchlistsInput = input<Array<Watchlist>>([], { alias: 'watchlists' });
  readonly watchlists = linkedSignal<Array<Watchlist>>(() => this.watchlistsInput());
  readonly symbol = input<SecuritySymbol>(undefined);
  readonly type = input<AddToWatchlistType>('dropdown');
  readonly watchListsLoaded = input<boolean>(false);
  readonly compactView = input<boolean>(false);
  readonly stretched = input<boolean>(false);

  readonly symbolAdded = output<void>();

  symbolAddedCompleted: boolean = false;
  symbolAddedFailed: boolean = false;
  menuOpened: boolean = false;

  private readonly mediaQueryService = inject(MediaQueryService);
  readonly isSmallScreen: Signal<boolean> = toSignal(
    this.mediaQueryService.observe(Breakpoint.SM).pipe(map((state: BreakpointState): boolean => state.matches)),
    { initialValue: false }
  );

  readonly overlayPosition: Signal<Array<AddToWatchlistPosition>> = computed((): Array<AddToWatchlistPosition> => {
    return this.isSmallScreen()
      ? ['end-bottom', 'end-top']
      : ['start-bottom', 'start-top'];
  });

  ngOnInit(): void {
    if (!this.watchListsLoaded()) {
      this.watchlistService.getAllWatchlists(false).subscribe((watchlists:  Array<Watchlist>): void => {
        this.watchlists.set(watchlists);
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  /**
   * Answers true if a given Watchlist is at max capacity, false otherwise.
   */
  isFull(watchlist: Watchlist): boolean {
    return WatchlistUtils.isWatchlistAtMaxCapacity(watchlist);
  }

  /**
   * Answers true if a given Watchlist contains the selected symbol.
   */
  hasSymbol(watchlist: Watchlist): boolean {
    return WatchlistUtils.hasSymbol(watchlist, this.symbol());
  }

  isDisabled(watchlist: Watchlist): boolean {
    return this.isFull(watchlist) || this.hasSymbol(watchlist);
  }

  /**
   * Add the selected symbol to a given Watchlist.
   */
  addSymbolToWatchlist(watchlist: Watchlist): void {
    if (!this.isFull(watchlist) && !this.hasSymbol(watchlist)) {
      WatchlistUtils.addToWatchlist(watchlist, this.symbol());
      this.watchlistService.saveWatchlist(watchlist).subscribe({
        next: (): void => {
          this.symbolAddedCompleted = true;
          this.symbolAdded.emit(undefined);
        },
        error: (): void => {
          this.symbolAddedFailed = true;
        },
        complete: (): void => {
          this.addSymbolToWatchlistComplete();
          this.changeDetectorRef.markForCheck();
        }
      });
    }
  }

  trackByWatchlistNumber(_: number, item: Watchlist): number {
    return item.number;
  }

  setMenuToggle(): void {
    this.menuOpened = !this.menuOpened;
  }

  private addSymbolToWatchlistComplete(): void {
    setTimeout((): void => {
      this.symbolAddedCompleted = false;
      this.symbolAddedFailed = false;
      this.changeDetectorRef.markForCheck();
    }, 3000);
  }
}
